import { Box, Paper, Typography } from "@mui/material";

export interface MetricPanelProps {
  title: string;
  subTitle: string;
  icon: React.ReactNode;
  mainValueLabel: string;
  mainValue: string | number;
  subValue1Label: string;
  subValue1: string | number;
  subValue2Label: string;
  subValue2: string | number;
  onClick?: () => void;
  isLoading?: boolean;
}

const MetricPanel: React.FC<MetricPanelProps> = ({
  title,
  subTitle,
  icon,
  mainValueLabel,
  mainValue,
  subValue1Label,
  subValue1,
  subValue2Label,
  subValue2,
  onClick,
  isLoading = false
}) => {
  return (
    <Paper
      sx={{
        p: { xs: 1.5, md: 2 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: 2,
        borderRadius: 2,
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? {
          bgcolor: "action.hover"
        } : {}
      }}
      onClick={onClick}
    >
      {/* Header with icon and title - same style as histograms */}
      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
        <Box sx={{ mr: 1 }}>{icon}</Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          <Typography variant="subtitle1" pb={0} mb={0}>{title}</Typography>
          <Typography variant="body2" color="text.disabled" pt={0} mt={0} sx={{ lineHeight: 1, fontWeight: "medium" }}>{subTitle}</Typography>
        </Box>
      </Box>

      {isLoading ? (
        // Loading state
        <Box sx={{ display: "flex", flexGrow: 1, justifyContent: "center", alignItems: "center" }}>
          <Box sx={{ display: "inline-block", position: "relative", width: 40, height: 40 }}>
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "4px solid rgba(25, 118, 210, 0.2)",
                borderRadius: "50%",
                borderTop: "4px solid rgba(25, 118, 210, 1)",
                animation: "spin 1s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" }
                }
              }}
            />
          </Box>
        </Box>
      ) : (
        <>
          {/* Main value */}
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "medium" }}>
              {mainValueLabel}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                textAlign: "left",
                fontWeight: "medium",
                color: "primary.main",
                fontSize: { xs: "1.75rem", md: "2rem" }
              }}
            >
              {mainValue}
            </Typography>
          </Box>

          {/* Sub values */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1, justifyContent: "center" }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "medium" }}>
                {subValue1Label}
              </Typography>
              <Typography variant="h6" color="primary.dark" sx={{ fontWeight: "bold", fontSize: { xs: "1rem", md: "1.15rem" } }}>
                {subValue1}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "medium" }}>
                {subValue2Label}
              </Typography>
              <Typography variant="h6" color="primary.dark" sx={{ fontWeight: "bold", fontSize: { xs: "1rem", md: "1.15rem" } }}>
                {subValue2}
              </Typography>
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default MetricPanel;
